import { useState } from 'react';

const STORAGE_KEY = 'lc_access_code';

export function getAccessCode() {
  return localStorage.getItem(STORAGE_KEY) || '';
}

export function clearAccessCode() {
  localStorage.removeItem(STORAGE_KEY);
}

export default function AccessGate({ children }) {
  const [code, setCode]     = useState('');
  const [error, setError]   = useState('');
  const [loading, setLoading] = useState(false);
  const [granted, setGranted] = useState(() => {
    // Skip access gate on localhost
    if (window.location.hostname === 'localhost') return true;
    // Skip if no access code is configured (production)
    if (!process.env.REACT_APP_ACCESS_CODE_REQUIRED) return true;
    // Already have a stored code
    return !!localStorage.getItem(STORAGE_KEY);
  });

  async function handleSubmit() {
    if (!code.trim()) return;
    setLoading(true);
    setError('');

    try {
      // Test the code against a protected endpoint
      const res = await fetch('/api/billing/status', {
        headers: {
          'X-Access-Code': code.trim(),
          'Authorization': 'Bearer invalid', // will get 401 auth but not 401 access
        }
      });

      // 401 with "access denied" means wrong code
      // Any other response (including 401 auth error) means code is correct
      if (res.status === 401) {
        const data = await res.json();
        if (data.error === 'access denied') {
          setError('Invalid access code');
          setLoading(false);
          return;
        }
      }

      // Code is valid
      localStorage.setItem(STORAGE_KEY, code.trim());
      setGranted(true);
    } catch {
      setError('Could not connect — try again');
    } finally {
      setLoading(false);
    }
  }

  if (granted) return children;

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--bg)',
      fontFamily: 'var(--font-sans)',
    }}>
      <div style={{
        background: 'var(--card)',
        border: '1.5px solid var(--border)',
        borderRadius: 16,
        padding: '48px 40px',
        maxWidth: 380,
        width: '100%',
        textAlign: 'center',
      }}>
        <p style={{ fontSize: 22, fontWeight: 300, marginBottom: 4 }}>
          Landlord<em>Comply</em>
        </p>
        <p style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 32 }}>
          Test environment — access restricted
        </p>

        <input
          type="password"
          placeholder="Enter access code"
          value={code}
          onChange={e => setCode(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSubmit()}
          style={{
            width: '100%',
            padding: '12px 16px',
            border: `1.5px solid ${error ? 'var(--danger)' : 'var(--border)'}`,
            borderRadius: 8,
            fontSize: 15,
            marginBottom: 12,
            background: 'var(--bg)',
            color: 'var(--ink)',
            boxSizing: 'border-box',
          }}
          autoFocus
        />

        {error && (
          <p style={{ fontSize: 13, color: 'var(--danger)', marginBottom: 12 }}>{error}</p>
        )}

        <button
          onClick={handleSubmit}
          disabled={loading || !code.trim()}
          style={{
            width: '100%',
            padding: '12px',
            background: 'var(--ink)',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            fontSize: 15,
            cursor: loading ? 'wait' : 'pointer',
            opacity: loading || !code.trim() ? 0.6 : 1,
          }}
        >
          {loading ? 'Checking...' : 'Enter'}
        </button>
      </div>
    </div>
  );
}